import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CommentInput } from 'src/app/models/server/comment-input';
import { FolderDocument } from 'src/app/models/server/folder-document';
import { DialogAddCommentData } from '../../components/dialogs/add-comment/models/dialog-add-comment-data';
import { IDetailTableData } from '../../models/interfaces/detail-table-data.interface';
import { ConnectedUsersStateService } from '../state/connected-users-state.service';
import { IFolderCommentViewerService } from './folder-comment-viewer-service.interface';
import { ServiceBase } from './service-base';

@Injectable({
    providedIn: 'root',
})
export class FolderCommentsService extends ServiceBase implements IFolderCommentViewerService {
    /**
     * Constructeur
     * */
    constructor(
        private http: HttpClient,
        public override connectedUserStateService: ConnectedUsersStateService
    ) {
        super(connectedUserStateService);
    }

    /**
     * Récupère les informations des documents d'un dossier
     * @param folderId: Id du dossier
     */
    public list(folderId: string): Observable<IDetailTableData[]> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.get<IDetailTableData[]>(this.baseUrl + `/api/organizations/${this.organizationId}/folders/${folderId}/comments`, httpOptions).pipe(map((x) => x));
    }

    /**
     * Ajout un commentaire à un dossier
     * @param viewId : Identifiant de la vue
     * @param folderId: identifiant du dossier
     * @param commentText : Le texte du commentaire
     * @returns : Le document contenant les commentaires
     */
    public add(viewId: number, folderId: string, commentText: DialogAddCommentData): Observable<FolderDocument> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http
            .post<FolderDocument>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/comments`, commentText, httpOptions)
            .pipe(
                map((x) => {
                    x.viewId = viewId;
                    return x;
                })
            );
    }

    /**
     * Récupère le document contenant les commentaires d'un dossier.
     * @param viewId : Identifiant de la vue
     * @param folderId: Id du dossier
     */
    public getComment(viewId: number, folderId: string): Observable<FolderDocument> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.get<FolderDocument>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/comments-document`, httpOptions).pipe(
            map((x) => {
                x.viewId = viewId;
                return x;
            })
        );
    }

    /**
     * Récupère le contenu d'un document contenant les commentaires d'un dossier.
     * @param folderId: Id du dossier
     * @param documentId: Id du document correspondant aux commentaires
     * @param fileId: Id du fichier contenant les commentaires
     */
    public getCommentContent(viewId: number, folderId: string, documentId: string, fileId: string): Observable<CommentInput[]> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http
            .get<CommentInput[]>(this.baseUrl + `/api/organizations/${this.organizationId}/folders/${folderId}/comments/${documentId}/${fileId}`, httpOptions)
            .pipe(map((x) => x));
    }

    /**
     * Met à jour un commentaire d'un dossier
     * @param viewId : Identifiant de la vue
     * @param folderId: identifiant du dossier
     * @param comment : Le commentaire modifié
     * @returns : Le document contenant les commentaires
     */
    public update(viewId: number, folderId: string, comment: CommentInput): Observable<FolderDocument> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        var data = new DialogAddCommentData(comment.text);

        return this.http
            .patch<FolderDocument>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/comments/${comment.id}`, data, httpOptions)
            .pipe(
                map((x) => {
                    x.viewId = viewId;
                    return x;
                })
            );
    }

    /**
     * Ajout d'un dossier
     * @param viewId : Identifiant de la vue
     * @param folderId: identifiant du dossier
     * @param comment : Le commentaire à supprimer
     * @returns : Le document contenant les commentaires
     */
    public delete(viewId: number, folderId: string, comment: CommentInput): Observable<FolderDocument> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http
            .delete<FolderDocument>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/comments/${comment.id}`, httpOptions)
            .pipe(
                map((x) => {
                    x.viewId = viewId;
                    return x;
                })
            );
    }
}
